import {
  normalisePagedResult,
  type CreateLeadPartnerRequest,
  type LeadPartner,
  type LeadPartnerCredentialResponse,
  type LeadPartnerStatus,
  type PagedResult,
  type RawPagedResult,
  type UpdateLeadPartnerRequest,
} from "@ir/types";
import { adminApi, request } from "./http";

export interface LeadPartnerQuery {
  searchTerm?: string;
  status?: LeadPartnerStatus | "";
  pageNumber?: number;
  pageSize?: number;
  sortBy?: "name" | "source" | "status" | "last_used_at" | "expires_at" | "created_at";
  sortDirection?: "asc" | "desc";
  signal?: AbortSignal;
}

export async function listLeadPartners({
  searchTerm = "",
  status = "",
  pageNumber = 1,
  pageSize = 25,
  sortBy = "created_at",
  sortDirection = "desc",
  signal,
}: LeadPartnerQuery = {}): Promise<PagedResult<LeadPartner>> {
  const response = await request(
    () =>
      adminApi.get<RawPagedResult<LeadPartner>>("/manage/lead-partners", {
        params: { searchTerm, status, pageNumber, pageSize, sortBy, sortDirection },
        signal,
      }),
    "Failed to load lead partners.",
  );
  return normalisePagedResult(response.data, pageNumber, pageSize);
}

export async function createLeadPartner(
  payload: CreateLeadPartnerRequest,
): Promise<LeadPartnerCredentialResponse> {
  const response = await request(
    () => adminApi.post<LeadPartnerCredentialResponse>("/manage/lead-partners", payload),
    "Failed to create that lead partner.",
  );
  return response.data;
}

export async function updateLeadPartnerStatus(
  partnerId: string,
  status: LeadPartnerStatus,
): Promise<LeadPartner> {
  const response = await request(
    () =>
      adminApi.patch<LeadPartner>(
        `/manage/lead-partners/${encodeURIComponent(partnerId)}/status`,
        { status },
      ),
    "Failed to update that lead partner.",
  );
  return response.data;
}

export async function rotateLeadPartnerKey(
  partnerId: string,
): Promise<LeadPartnerCredentialResponse> {
  const response = await request(
    () =>
      adminApi.post<LeadPartnerCredentialResponse>(
        `/manage/lead-partners/${encodeURIComponent(partnerId)}/rotate-key`,
        {},
      ),
    "Failed to rotate that API key.",
  );
  return response.data;
}

export async function updateLeadPartnerSettings(
  partnerId: string,
  payload: UpdateLeadPartnerRequest,
): Promise<LeadPartner> {
  const response = await request(
    () =>
      adminApi.patch<LeadPartner>(
        `/manage/lead-partners/${encodeURIComponent(partnerId)}/settings`,
        payload,
      ),
    "Failed to update that lead partner's settings.",
  );
  return response.data;
}
